import { useState } from 'react'
import LazyMarketingCredit from './LazyMarketingCredit.jsx'
import { Link } from 'react-router-dom'
import { Button, Input } from './primitives/index.js'
import { api } from '../lib/api.js'
import { useProducts } from '../context/ProductsContext.jsx'
import TurnstileWidget, { turnstileEnabled } from './TurnstileWidget.jsx'
import './Footer.css'

/** Field names mirror Prod's Footer.jsx FOOTER_DEFAULTS — see siteContent.footer. */
const FOOTER_DEFAULTS = {
  tagline: 'Research-grade peptides and beauty formulations, precision-manufactured and rigorously tested.',
  newsletterHeadline: 'Stay in the loop',
  newsletterBody: '',
  copyrightSuffix: '',
  disclaimer:
    'Products sold by LooksLab are not intended for human or veterinary use, diagnosis, treatment, cure, or prevention of any disease. Not for household use.',
}

// Pre-admin-save fallback — same shape Prod expects from siteContent.footerColumns.
const DEFAULT_COLUMNS = [
  {
    title: 'Shop',
    links: [
      { label: 'Shop All', to: '/catalog' },
      { label: 'Track an order', to: '/track' },
    ],
  },
  {
    title: 'Account',
    links: [
      { label: 'Sign in', to: '/account/login' },
      { label: 'Wishlist', to: '/account?tab=wishlist' },
      { label: 'Referrals', to: '/account?tab=referrals' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Terms & Conditions', to: '/terms' },
      { label: 'Privacy Policy', to: '/privacy' },
      { label: 'System Status', to: '/status' },
      { label: 'Contact', to: 'mailto:legal@lookslab.shop' },
    ],
  },
]

function FooterLink({ link }) {
  const to = link.to ?? link.url ?? link.href ?? '#'
  const isAnchor = to.includes('#') || to.includes('://')
  return isAnchor ? <a href={to}>{link.label}</a> : <Link to={to}>{link.label}</Link>
}

export default function Footer() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState('idle')
  const [errMsg, setErrMsg] = useState('')
  // Only mount the Turnstile widget once the visitor starts typing — keeps
  // the footer clean on every page until it's actually needed.
  const [engaged, setEngaged] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState(null)
  const { siteContent, branding } = useProducts()

  const f = { ...FOOTER_DEFAULTS, ...(siteContent?.footer ?? {}) }
  const columns = siteContent?.footerColumns?.length ? siteContent.footerColumns : DEFAULT_COLUMNS
  // Holiday logo takes priority over the white logo; both fall back to the static file.
  // The static file is a dark logotype meant for light backgrounds, so invert it here
  // (a dark footer) when no admin-configured logo is set.
  const usingFallbackLogo = !branding?.logoHoliday?.url && !branding?.logoWhite?.url
  const logoSrc = branding?.logoHoliday?.url ?? branding?.logoWhite?.url ?? '/logos/lookslab-logo.png'

  async function handleSubscribe(e) {
    e.preventDefault()
    if (!email.trim()) return
    if (turnstileEnabled && !turnstileToken) {
      setStatus('error')
      setErrMsg('Please complete the verification check.')
      return
    }
    setStatus('loading')
    try {
      await api.subscribeNewsletter(email.trim(), turnstileToken)
      setStatus('success')
      setEmail('')
    } catch (err) {
      setStatus('error')
      setErrMsg(err.message ?? 'Something went wrong. Please try again.')
      if (turnstileEnabled && window.turnstile) {
        try { window.turnstile.reset() } catch { /* ignore */ }
        setTurnstileToken(null)
      }
    }
  }

  return (
    <footer id="site-footer" className="footer">
      <div className="container footer__grid">
        <div className="footer__brand">
          <img
            src={logoSrc}
            alt="LooksLab"
            className="footer__logo-img"
            width={131}
            height={64}
            style={usingFallbackLogo ? { filter: 'invert(1)' } : undefined}
          />
          <p className="footer__tagline">{f.tagline}</p>
        </div>

        {columns.map((col) => (
          <div key={col.title} className="footer__col">
            <h3>{col.title}</h3>
            {col.links.map((l) => (
              <FooterLink key={l.label} link={l} />
            ))}
          </div>
        ))}

        <div className="footer__col footer__newsletter">
          <h3>{f.newsletterHeadline}</h3>
          {f.newsletterBody && <p className="footer__newsletter-body">{f.newsletterBody}</p>}
          <form onSubmit={handleSubscribe}>
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (!engaged) setEngaged(true) }}
              required
            />
            <Button type="submit" variant="secondary" size="sm" loading={status === 'loading'}>
              {status === 'success' ? 'Subscribed' : 'Subscribe'}
            </Button>
            {status === 'error' && errMsg && <p className="footer__newsletter-error">{errMsg}</p>}
            {turnstileEnabled && engaged && (
              <TurnstileWidget onToken={setTurnstileToken} />
            )}
          </form>
        </div>
      </div>

      <div className="container footer__bottom">
        <span>&copy; {new Date().getFullYear()} LooksLab. All rights reserved. {f.copyrightSuffix}</span>
        <LazyMarketingCredit className="footer__credit" />
      </div>

      <div className="container footer__disclaimer">
        <strong>Research use only.</strong> {f.disclaimer}
      </div>
    </footer>
  )
}
